// UserSheet.jsx — one account, everything about it, and every action that can
// be taken on it. Reached from Users, from an org's member list and from
// Support, so the same account looks and behaves the same wherever you found it.
//
// The actions are ordered by blast radius: role and activation (routine),
// then force sign-out (disruptive but reversible by signing in again), then
// unbind Google (rare, and the only one that touches the identity binding).

import React from 'react';
import { api, errorText } from '../api';
import { useAuth } from '../AuthProvider';
import { useAsync } from '../useAsync';
import { FLAG_COPY, ago, jobTone, stamp } from '../format';
import { canResendActivation, emailOutcome } from '../activationEmail';
import { useToast } from './Toast';
import {
  Button, ConfirmSheet, ErrorState, Field, Icon, KV, Note, Pill, Sheet, Skeleton,
} from './primitives';

const ROLES = ['operator', 'approver', 'admin'];

// The platform ladder. Ordered weakest → strongest; the copy says what each
// rung actually lets a person do, because "superuser" and "god" mean nothing
// on their own to whoever is reading the screen at 2am.
const ACCESS = [
  { value: 'member', label: 'Member',
    hint: 'Normal tenant user. No access to the control center.' },
  { value: 'superuser', label: 'Superuser',
    hint: 'Full control center: every org, every user, every job. Cannot grant access to others.' },
  { value: 'god', label: 'God',
    hint: 'Everything a superuser can do, plus granting and revoking platform access.' },
];

export default function UserSheet({ userId, onClose, onChanged }) {
  const toast = useToast();
  const me = useAuth().user;
  const iAmGod = me?.access_level === 'god';
  const { data, error, loading, reload } = useAsync(() => api.user(userId), [userId]);
  const [confirm, setConfirm] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const user = data?.user;
  const org = data?.org;

  const run = async (fn, okText) => {
    setBusy(true);
    try {
      await fn();
      toast.ok(okText);
      setConfirm(null);
      reload();
      onChanged?.();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const setRole = (role) => run(
    () => api.patchUser(user.org_id, user.id, { role }),
    `Role is now ${role}.`);

  const setActive = (is_active) => run(
    () => api.patchUser(user.org_id, user.id, { is_active }),
    is_active ? 'User reactivated.' : 'User deactivated and signed out.');

  const forceSignOut = () => run(async () => {
    const r = await api.signOutUser(user.id);
    return r;
  }, 'Every session revoked.');

  const unbind = () => run(
    () => api.unbindSso(user.id),
    'Google binding cleared — the next sign-in will re-bind.');

  // #73. Not through `run`: its toast is fixed text, and here the backend's
  // outcome IS the message — sent, or not sent and why.
  const resendActivation = async () => {
    setBusy(true);
    try {
      const r = await api.resendActivationEmail(user.org_id, user.id);
      const line = emailOutcome(r && r.activation_email)
        || { text: 'Done.', tone: 'ok' };
      toast(line.text, line.tone);
      reload();
      onChanged?.();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const setAccess = (level) => run(
    () => api.setAccessLevel(user.id, level),
    `Platform access set to ${level}. They must sign in again.`);

  return (
    <>
      <Sheet title={user?.display_name || user?.email || 'User'}
             subtitle={user ? `${user.email} · ${org?.name || '—'}` : null}
             onClose={onClose}>
        {loading && !data && <Skeleton rows={4} />}
        {error && <ErrorState error={error} text={errorText(error)} onRetry={reload} />}

        {data && (
          <div className="stack">
            <div className="row row-wrap" style={{ gap: 6 }}>
              <Pill tone={user.is_active ? 'ok' : 'danger'}>
                {user.is_active ? 'active' : 'deactivated'}
              </Pill>
              <Pill tone="accent">{user.role}</Pill>
              {data.user.access_level !== 'member' && (
                <Pill tone="accent">{data.user.access_level}</Pill>
              )}
              <Pill tone={user.sso_bound ? '' : 'warn'}>
                {user.sso_bound ? 'google bound' : 'not bound'}
              </Pill>
              <Pill tone={data.sessions.active_families > 0 ? 'ok' : ''}>
                {data.sessions.active_families} session{data.sessions.active_families === 1 ? '' : 's'}
              </Pill>
            </div>

            {data.flags?.filter((f) => FLAG_COPY[f]?.tone).map((f) => (
              <Note key={f} tone={FLAG_COPY[f].tone} title={FLAG_COPY[f].label}>
                {FLAG_COPY[f].hint}
              </Note>
            ))}

            <KV items={[
              ['Email', <span key="e" className="mono" style={{ fontSize: 12 }}>{user.email}</span>],
              ['Org', org ? `${org.name}${org.is_active ? '' : ' (deactivated)'}` : user.org_id],
              ['Last login', data.sessions.last_login_at
                ? `${ago(data.sessions.last_login_at)} · ${stamp(data.sessions.last_login_at)}`
                : 'never'],
              ['Provisioned', user.created_at ? stamp(user.created_at) : null],
              ['User id', <span key="i" className="mono" style={{ fontSize: 11 }}>{user.id}</span>],
            ]} />

            {/* ── actions ── */}
            <div className="col" style={{ gap: 9 }}>
              <h2>Actions</h2>

              <Field label="Role" hint="Applies immediately; the user picks it up within one access-token lifetime (≤15 min).">
                <select className="select" value={user.role} disabled={busy}
                        onChange={(e) => setRole(e.target.value)}>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>

              <div className="btn-row">
                <Button small disabled={busy || !canResendActivation(user, org)}
                        onClick={resendActivation}>
                  Resend activation email
                </Button>
                {user.is_active ? (
                  <Button variant="danger" small disabled={busy}
                          onClick={() => setConfirm({
                            kind: 'deactivate',
                            title: 'Deactivate this user?',
                            body: `${user.email} will be signed out everywhere and cannot sign in again until reactivated.`,
                            label: 'Deactivate',
                            danger: true,
                            fn: () => setActive(false),
                          })}>
                    Deactivate
                  </Button>
                ) : (
                  <Button variant="primary" small disabled={busy}
                          onClick={() => setActive(true)}>
                    Reactivate
                  </Button>
                )}

                <Button small disabled={busy || data.sessions.active_families === 0}
                        onClick={() => setConfirm({
                          kind: 'signout',
                          title: 'Sign this user out everywhere?',
                          body: `Revokes all ${data.sessions.active_families} refresh session(s) for ${user.email}. They can sign in again immediately — use this for a lost or shared device.`,
                          label: 'Revoke sessions',
                          fn: forceSignOut,
                        })}>
                  Force sign-out
                </Button>

                <Button small disabled={busy || !user.sso_bound}
                        onClick={() => setConfirm({
                          kind: 'unbind',
                          title: 'Unbind the Google account?',
                          body: `Clears the Google identity bound to ${user.email} and revokes their sessions. The NEXT Google account to sign in with this email claims it — only do this when the wrong account is bound (an “account conflict” at sign-in).`,
                          label: 'Unbind',
                          danger: true,
                          word: user.email,
                          fn: unbind,
                        })}>
                  Unbind Google
                </Button>
              </div>
            </div>

            {/* ── platform access (god only) ── */}
            {iAmGod && (
              <div className="col" style={{ gap: 9 }}>
                <h2>Platform access</h2>
                {user.id === me?.id ? (
                  <Note title="This is you">
                    You cannot change your own platform access — that is the
                    guard that stops the last god locking everyone out.
                  </Note>
                ) : (
                  <>
                    <div className="chips">
                      {ACCESS.map((a) => (
                        <button key={a.value} type="button" className="chip"
                                aria-pressed={data.user.access_level === a.value}
                                disabled={busy}
                                onClick={() => (a.value === data.user.access_level
                                  ? null
                                  : setConfirm({
                                      title: `Set access to ${a.label}?`,
                                      body: `${user.email} becomes a ${a.label.toLowerCase()}. ${a.hint} They are signed out everywhere and must sign in again for it to take effect.`,
                                      label: `Make ${a.label}`,
                                      danger: a.value === 'god',
                                      word: a.value === 'god' ? user.email : null,
                                      fn: () => setAccess(a.value),
                                    }))}>
                          {a.label}
                        </button>
                      ))}
                    </div>
                    <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
                      {ACCESS.find((a) => a.value === data.user.access_level)?.hint}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── recent work ── */}
            {data.jobs.recent.length > 0 && (
              <div className="col" style={{ gap: 7 }}>
                <h2>Recent jobs</h2>
                <div className="card" style={{ background: 'var(--surface)' }}>
                  <div className="list">
                    {data.jobs.recent.slice(0, 5).map((j) => (
                      <div key={j.job_id} className="list-row">
                        <Pill tone={jobTone(j.status)}>{j.status}</Pill>
                        <div className="col" style={{ flex: 1 }}>
                          <span className="mono truncate" style={{ fontSize: 11.5 }}>{j.job_id}</span>
                          <span className="dim" style={{ fontSize: 11 }}>
                            {j.kind} · {ago(j.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {data.recent_events.length > 0 && (
              <div className="col" style={{ gap: 7 }}>
                <h2>Recent activity</h2>
                <div className="stack" style={{ gap: 5 }}>
                  {data.recent_events.slice(0, 8).map((e) => (
                    <div key={e.id} className="row" style={{ gap: 8 }}>
                      <span className="mono dim nowrap" style={{ fontSize: 10.5 }}>
                        {stamp(e.ts)}
                      </span>
                      <span className="mono truncate" style={{ fontSize: 11.5 }}>
                        {e.event_type}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Sheet>

      {confirm && (
        <ConfirmSheet title={confirm.title} body={confirm.body}
                      confirmLabel={confirm.label} danger={confirm.danger}
                      confirmWord={confirm.word} busy={busy}
                      onConfirm={confirm.fn} onClose={() => setConfirm(null)} />
      )}
    </>
  );
}

export { Icon };
